import {
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    List,
    ListItem,
    InputAdornment,
    Option,
    SelectField,
    TextField,
    Typography,
} from '@deere/fuel-react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox.jsx';
import { PromptValidState } from '../../components/navigation/PromptValidState.jsx';
import { PromptInvalidState } from '../../components/navigation/PromptInvalidState.jsx';
import { LoggedInContext } from '../../context/LoggedInContext.jsx';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import { WorkforceContext } from '../../context/WorkforceContext';
import { ALLOWED_BIDDING_GROUPS_TO_WITHDRAW } from '../../constants';
import styles from './WorkforceMyActiveJobBids.module.css';

const sortJobBids = (bids, sortBy, sortDirection) =>
    [...bids].sort((firstBid, secondBid) => {
        const firstValue = firstBid[sortBy];
        const secondValue = secondBid[sortBy];
        const comparison = ['id', 'preference'].includes(sortBy)
            ? Number(firstValue) - Number(secondValue)
            : String(firstValue ?? '').localeCompare(
                  String(secondValue ?? ''),
                  undefined,
                  { numeric: true, sensitivity: 'base' }
              );

        return sortDirection === 'asc' ? comparison : -comparison;
    });

const sortJobBidsWithinStatusGroups = (bids, sortBy, sortDirection) => [
    ...sortJobBids(
        bids.filter((bid) => bid.status !== 'WITHDRAWN'),
        sortBy,
        sortDirection
    ),
    ...sortJobBids(
        bids.filter((bid) => bid.status === 'WITHDRAWN'),
        sortBy,
        sortDirection
    ),
];

const filterJobBids = (bids, searchTerm) => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) {
        return bids;
    }

    return bids.filter((bid) =>
        [bid.id, bid.facility, bid.department, bid.classification].some(
            (value) =>
                String(value ?? '')
                    .toLowerCase()
                    .includes(normalizedSearchTerm)
        )
    );
};

const ActiveJobRow = ({
    activeJob,
    numberOfJobs,
    updatePref,
    inBidWithdrawal,
    inPostWithdrawal,
}) => {
    const navigate = useNavigate();
    const numberOfOptions = new Array(numberOfJobs);
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));
    for (let i = 0; i < numberOfJobs; i++) {
        numberOfOptions[i] = i + 1;
    }

    const handleChange = (event) => {
        updatePref(activeJob, event.target.value);
    };

    const navigateTo = () => {
        let navigateURL = '/active-job-openings/';
        if (
            inBidWithdrawal ||
            inPostWithdrawal ||
            ALLOWED_BIDDING_GROUPS_TO_WITHDRAW.includes(
                activeJob.biddingGroupId
            )
        ) {
            //NAPDC
            navigateURL = '/my-job-bids/';
        }
        navigate(navigateURL + activeJob.id);
    };

    return (
        <ListItem className={styles.jobBidRow}>
            <Card className={styles.jobBidRowCard}>
                <Grid container>
                    {!inBidWithdrawal && !inPostWithdrawal && (
                        <Grid
                            item
                            sm={2}
                            xs={2}
                            className={styles.dropdownContainer}
                            order={matches ? 1 : 2}
                        >
                            <SelectField
                                value={activeJob.preference}
                                onChange={handleChange}
                                fullWidth={false}
                                error={activeJob.duplicate}
                            >
                                {numberOfOptions.map((numberOfOpt, index) => (
                                    <Option key={index} value={numberOfOpt}>
                                        {numberOfOpt}
                                    </Option>
                                ))}
                            </SelectField>
                        </Grid>
                    )}
                    {(inBidWithdrawal || inPostWithdrawal) && (
                        <Grid item sm={2} xs={2} className={styles.center}>
                            <Typography size="3xl">
                                {activeJob.preference}
                            </Typography>
                        </Grid>
                    )}

                    <Grid container item xs={10} order={1} onClick={navigateTo}>
                        <Grid item sm={3} className={styles.jobBidRowTitle}>
                            <span>
                                {' '}
                                {activeJob.classification} #{activeJob.id}{' '}
                            </span>
                            <span>- ({activeJob.noOfOpenings}) openings</span>
                        </Grid>
                        <Grid item sm={9}>
                            <span>
                                {(inBidWithdrawal ||
                                    inPostWithdrawal ||
                                    ALLOWED_BIDDING_GROUPS_TO_WITHDRAW.includes(
                                        activeJob.biddingGroupId
                                    )) &&
                                    activeJob.status === 'WITHDRAWN' && (
                                        <Badge
                                            startIcon
                                            /*={<CheckCircle />}*/ variant="warning"
                                            className={styles.statusBadge}
                                        >
                                            Bid Withdrawn
                                        </Badge>
                                    )}
                                {(inBidWithdrawal ||
                                    inPostWithdrawal ||
                                    ALLOWED_BIDDING_GROUPS_TO_WITHDRAW.includes(
                                        activeJob.biddingGroupId
                                    )) &&
                                    activeJob.status === 'SUBMITTED' && (
                                        <Typography
                                            fontWeight="bold"
                                            variant="h5"
                                        >
                                            Ranked {activeJob.adminRank} out of{' '}
                                            {activeJob.totalBidders} bidders
                                        </Typography>
                                    )}
                            </span>
                        </Grid>
                        <Grid item xs={12} className={styles.jobBidInfoSection}>
                            <span>Facility: {activeJob.facility}</span>
                            <span>Dept: {activeJob.department}</span>
                            <span>Job Grade: {activeJob.jobGrade}</span>
                            <span>
                                Bid Withdrawal Starts:{' '}
                                {DateTime.fromISO(activeJob.endDate).toFormat(
                                    'LL/dd/yy'
                                )}
                            </span>
                            <span>
                                Bid Withdrawal Ends:{' '}
                                {DateTime.fromISO(
                                    activeJob.bidWithDrawalEndDt
                                ).toFormat('LL/dd/yy')}
                            </span>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>
        </ListItem>
    );
};

export const WorkforceMyActiveJobBids = () => {
    const { loading, activeJobBids, setActiveJobBids, error } =
        useContext(WorkforceContext);
    const { makeRequest, authState } = useContext(LoggedInContext);
    const [activeJobBidsLocal, setActiveJobBidsLocal] = useState([]);
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [inBidWithdrawal, setInBidWithdrawal] = useState(false);
    const [inPostWithdrawal, setInPostWithdrawal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('preference');
    const [sortDirection, setSortDirection] = useState('asc');

    const updatePref = (activeJob, preferenceVal) => {
        setIsFormDirty(true);

        const updatedActiveJobs = activeJobBidsLocal.map((job) => {
            if (job.id === activeJob.id) {
                return { ...job, preference: preferenceVal, duplicate: false };
            }
            return { ...job, duplicate: false };
        });
        setActiveJobBidsLocal(updatedActiveJobs);
        const duplicatePrefExists = checkDuplicatePref(
            updatedActiveJobs,
            false
        );
        if (duplicatePrefExists) {
            setAlertVariant('error');
            setAlertMsg(
                'You may not duplicate numbers in your ranking order. '
            );
        } else {
            setAlertVariant(null);
        }
    };
    const areAllFieldsValid = () => {
        return isFormDirty;
    };

    const checkDuplicatePref = (bids, duplicatePrefExists) => {
        for (let i = 0; i < bids.length; i++) {
            for (let j = i + 1; j < bids.length; j++) {
                if (bids[i].preference === bids[j].preference) {
                    duplicatePrefExists = true;
                    bids[i].duplicate = true;
                }
            }
        }
        return duplicatePrefExists;
    };

    const saveRankApiCall = async () => {
        const duplicatePrefExists = checkDuplicatePref(
            activeJobBidsLocal,
            false
        );
        const dup = [...activeJobBidsLocal];
        setActiveJobBidsLocal(dup);
        if (duplicatePrefExists) {
            window.scrollTo(0, 0);
            setAlertVariant('error');
            setAlertMsg(
                'You may not duplicate numbers in your ranking order. '
            );
        } else {
            const response = await makeRequest(
                process.env.REACT_APP_API_ENDPOINT + '/bidder/saveMyRankPref',
                {
                    method: 'POST',
                    body: JSON.stringify(activeJobBidsLocal),
                }
            );

            if (response.status === 200) {
                window.scrollTo(0, 0);
                setAlertVariant('success');
                setIsFormDirty(false);
                setAlertMsg(
                    'You have successfully saved your job bid rankings. You may change your rankings until the job closes and the bid withdrawal period starts.'
                );
                const sortedActiveJobBids = [...activeJobBidsLocal].sort(
                    (a, b) => a.preference - b.preference
                );
                setActiveJobBids(sortedActiveJobBids);
                setActiveJobBidsLocal(sortedActiveJobBids);
            } else {
                window.scrollTo(0, 0);
                setAlertVariant('error');
                setAlertMsg(
                    errorBannerFromResponse(
                        response,
                        'Your job bid rankings were not saved. Please try again later.'
                    ).message
                );
            }
        }
    };

    useEffect(() => {
        if (error) {
            setAlertVariant('error');
            setAlertMsg(
                errorBannerFromResponse(
                    error,
                    'Your job bids cannot be loaded at this time. Please try again later.'
                ).message
            );
        }

        if (!error && !loading && activeJobBids) {
            setActiveJobBidsLocal(activeJobBids);
            if (
                activeJobBids[0] &&
                DateTime.fromISO(activeJobBids[0].endDate) < DateTime.now() &&
                DateTime.fromISO(activeJobBids[0].bidWithDrawalEndDt) >
                    DateTime.now()
            ) {
                setInBidWithdrawal(true);
                setInPostWithdrawal(false);
                setAlertMsg(
                    'Jobs are currently in the Bid Withdrawal phase. Cancel your bid by clicking on the job number and then clicking on the Withdraw My Bid button.'
                );
                setAlertVariant('info');
            } else if (
                activeJobBids[0] &&
                DateTime.fromISO(activeJobBids[0].bidWithDrawalEndDt) <
                    DateTime.now()
            ) {
                setInBidWithdrawal(false);
                setInPostWithdrawal(true);
                setAlertMsg(
                    'These jobs are closed. Your bid rankings are locked in and job assignments will be determined in the near future.'
                );
                setAlertVariant('warning');
            }
        }
    }, [authState, activeJobBids, error, loading]);

    const displayedActiveJobBids = sortJobBidsWithinStatusGroups(
        filterJobBids(activeJobBidsLocal, searchTerm),
        sortBy,
        sortDirection
    );

    return (
        <div className={styles.parentContainer}>
            <Box className={styles.contentToContainer}>
                <Box>
                    {alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                </Box>
                <br></br>
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    <Typography variant="h1" className={styles.heading}>
                        My Job Bids
                    </Typography>
                </Box>
                <Box className={styles.jobDirectionsBox}>
                    {!inBidWithdrawal && !inPostWithdrawal && (
                        <Typography>
                            Rank your job bids by setting your ranking order,
                            with #1 as your most desired job. Do not duplicate
                            numbers in your ranking order.
                            <span className={styles.jobDirections}>
                                {' '}
                                You must complete your rankings before the bid
                                withdrawal starts.
                            </span>
                        </Typography>
                    )}
                    {inBidWithdrawal && (
                        <Typography>
                            Your bid rankings are now locked. You may cancel
                            your bid on a job during the Bid Withdrawal period
                            only. All job bid rankings are based on seniority
                            date.
                        </Typography>
                    )}
                    {inPostWithdrawal && (
                        <Typography>
                            You can click on the job number to view the
                            successful bidders for each job in your list.
                        </Typography>
                    )}
                </Box>
                {loading && (
                    <>
                        <Typography>
                            {' '}
                            Your request is being processed and is connecting to
                            Employee Central to display the employee seniority
                            rankings compared to other bidders. Your patience is
                            appreciated.
                        </Typography>
                        <CircularProgress
                            size="lg"
                            variant="indeterminate"
                            title={'Loading'}
                        />
                    </>
                )}
                {!loading && activeJobBidsLocal.length === 0 && (
                    <Card>
                        <CardContent>
                            <Typography gutterBottom variant="h4">
                                You do not have any active job bids at this
                                time.
                            </Typography>
                        </CardContent>
                    </Card>
                )}
                {!loading && activeJobBidsLocal.length > 0 && (
                    <>
                        <Box className={styles.filterHeader}>
                            <Box className={styles.searchField}>
                                <TextField
                                    type="text"
                                    size="small"
                                    placeholder="Search by job number, facility, department, classification"
                                    value={searchTerm}
                                    onChange={(event) =>
                                        setSearchTerm(event.target.value)
                                    }
                                    fullWidth
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    }
                                />
                            </Box>
                            <Box className={styles.sortControls}>
                                <SelectField
                                    label="Sort by"
                                    inputProps={{ 'aria-label': 'Sort by' }}
                                    value={sortBy}
                                    onChange={(event) =>
                                        setSortBy(event.target.value)
                                    }
                                >
                                    <Option value="preference">
                                        Preference
                                    </Option>
                                    <Option value="id">Job Number</Option>
                                    <Option value="facility">Facility</Option>
                                    <Option value="department">
                                        Department
                                    </Option>
                                    <Option value="classification">
                                        Classification
                                    </Option>
                                </SelectField>
                                <IconButton
                                    aria-label={
                                        sortDirection === 'asc'
                                            ? 'Sort descending'
                                            : 'Sort ascending'
                                    }
                                    title={
                                        sortDirection === 'asc'
                                            ? 'Sort descending'
                                            : 'Sort ascending'
                                    }
                                    onClick={() =>
                                        setSortDirection((direction) =>
                                            direction === 'asc' ? 'desc' : 'asc'
                                        )
                                    }
                                >
                                    {sortDirection === 'asc' ? (
                                        <ArrowDownwardIcon />
                                    ) : (
                                        <ArrowUpwardIcon />
                                    )}
                                </IconButton>
                            </Box>
                        </Box>
                        {displayedActiveJobBids.length > 0 ? (
                            <List>
                                {displayedActiveJobBids.map((activeJob) => (
                                    <ActiveJobRow
                                        activeJob={activeJob}
                                        key={activeJob.id}
                                        numberOfJobs={activeJobBidsLocal.length}
                                        updatePref={updatePref}
                                        inBidWithdrawal={inBidWithdrawal}
                                        inPostWithdrawal={inPostWithdrawal}
                                    />
                                ))}
                            </List>
                        ) : (
                            <Card>
                                <CardContent>
                                    <Typography gutterBottom variant="h4">
                                        No job bids match your search.
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                        {!inBidWithdrawal && !inPostWithdrawal && (
                            <Box
                                mt={2}
                                justifyContent="flex-end"
                                justifyItems="flex-end"
                                display="flex"
                            >
                                {alertVariant && (
                                    <PromptInvalidState
                                        isFormChanged={isFormDirty}
                                        areAllFieldsValid={areAllFieldsValid}
                                    />
                                )}
                                {!alertVariant && (
                                    <PromptValidState
                                        isFormChanged={isFormDirty}
                                        saveChanges={saveRankApiCall}
                                        areAllFieldsValid={areAllFieldsValid}
                                        message={
                                            'If you leave the page without clicking Save, your rankings will revert back to your last preference order, with the new job ranked as your last choice.'
                                        }
                                    />
                                )}

                                <Button
                                    variant="primary"
                                    onClick={saveRankApiCall}
                                    style={{ marginRight: '0.5rem' }}
                                >
                                    Save My Rankings
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </div>
    );
};
